/**
 * Multi-tenant configuration.
 * Tenant is resolved from window.location.hostname at runtime.
 *
 * Local domains:   admin.dhgriffinapp.local  /  admin.gicc.local
 * Production:      admin.dhgriffinapp.com    /  admin.gicc.app
 * Dev server:      localhost:3000 (dhgriffin) / localhost:3001 (gicc via REACT_APP_TENANT=gicc)
 */

const host = window.location.hostname.toLowerCase();
const isLocal = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
const protocol = window.location.protocol; // 'http:' or 'https:'

const TENANTS = {
    dhgriffin: {
        id: 'dhgriffin',
        dbName: 'dhgriffin',
        adminDomain: 'admin.dhgriffinapp.com',
        adminLocalDomain: 'admin.dhgriffinapp.local',
        wssUrl: isLocal ? 'ws://localhost:8080' : 'wss://wss.dhgriffinapp.com',
        logoUrl: '/images/logo.png',
        companyName: 'D.H. GRIFFIN COMPANIES',
        companyNameShort: 'D.H. Griffin',
        companyNameFormal: 'D.H. Griffin Companies',
        companyCode: 'DHGC',
        companyCodeShort: 'DHG',
        legalName: 'D. H. GRIFFIN CONTRACTING, INC.',
        legalNameShort: 'DHG',
        appTitle: 'DH Griffin App',
        appTitleShort: 'DHGriffin',
        themeColor: '#2962FF',
        // Company info for PDF header/footer (mirrors config/tenants/dhgriffin.yaml).
        pdf: {
            companyName: 'D. H. GRIFFIN CONTRACTING, Inc.',
            tagline: 'Machinery Installation Specialists',
            servicesLine: 'Turnkey Plant Relocation, Complete Millwright Services, Riggers, Electricians,<br/>Panel Fabrication, Plant Maintenance, &amp; Demolition',
            address: '974 Berry Shoals Road, Duncan, SC 29334',
            phone: '(864) 848-1885',
            fax: '(864) 848-1255',
        },
    },
    gicc: {
        id: 'gicc',
        dbName: 'gicc',
        adminDomain: 'admin.gicc.app',
        adminLocalDomain: 'admin.gicc.local',
        wssUrl: isLocal ? 'ws://localhost:8080' : 'wss://wss.gicc.app',
        logoUrl: '/images/tenants/gicc/logo.png',
        logoHeaderUrl: '/images/tenants/gicc/logo-white.png',
        companyName: 'GRIFFIN INDUSTRIAL CONTRACTING COMPANY',
        companyNameShort: 'GICC',
        companyNameFormal: 'Griffin Industrial Contracting Company',
        companyCode: 'GICC',
        companyCodeShort: 'GICC',
        legalName: 'GRIFFIN INDUSTRIAL CONTRACTING COMPANY',
        legalNameShort: 'GICC',
        appTitle: 'GICC App',
        appTitleShort: 'GICC',
        themeColor: '#1C1C1C',
        // Company info for PDF header/footer (mirrors config/tenants/gicc.yaml).
        pdf: {
            companyName: 'GRIFFIN INDUSTRIAL CONTRACTING COMPANY',
            tagline: 'Industrial Contracting Solutions',
            servicesLine: '',
            address: '4716 Hilltop Rd, Greensboro, NC 27407',
            phone: '(888) 336-3366',
            fax: '',
        },
    },
};

function resolveTenant() {
    // Environment variable override for local dev (set via REACT_APP_TENANT=gicc)
    const envTenant = process.env.REACT_APP_TENANT;
    if (envTenant === 'gicc') return TENANTS.gicc;
    if (envTenant === 'dhgriffin') return TENANTS.dhgriffin;

    // Resolve from hostname
    if (host.includes('gicc')) return TENANTS.gicc;

    // Default — dhgriffin (includes localhost, dhgriffinapp.local, dhgriffinapp.com)
    return TENANTS.dhgriffin;
}

export const tenant = resolveTenant();

// Build API URL: always absolute — frontend and backend on different domains
function buildApiUrl() {
    const isDevServer = host === 'localhost' || host === '127.0.0.1';
    if (isDevServer || host.endsWith('.local')) {
        return `http://${tenant.adminLocalDomain}/api`;
    }
    // Production — frontend on direct domain, API on admin subdomain
    return `https://${tenant.adminDomain}/api`;
}

// Convenience exports
export const TENANT_ID = tenant.id;
export const TENANT_DB_NAME = tenant.dbName;
export const TENANT_API_URL = buildApiUrl();
export const TENANT_WSS_URL = tenant.wssUrl;
export const TENANT_LOGO_URL = tenant.logoUrl;
export const TENANT_LOGO_HEADER_URL = tenant.logoHeaderUrl || tenant.logoUrl;
export const TENANT_COMPANY_NAME = tenant.companyName;
export const TENANT_COMPANY_SHORT = tenant.companyNameShort;
export const TENANT_COMPANY_FORMAL = tenant.companyNameFormal;
export const TENANT_APP_TITLE = tenant.appTitle;
export const TENANT_APP_TITLE_SHORT = tenant.appTitleShort;
export const TENANT_THEME_COLOR = tenant.themeColor;
export const TENANT_COMPANY_CODE = tenant.companyCode;
export const TENANT_COMPANY_CODE_SHORT = tenant.companyCodeShort;
export const TENANT_LEGAL_NAME = tenant.legalName;
export const TENANT_LEGAL_NAME_SHORT = tenant.legalNameShort;
// Company info block for PDF header/footer (logo + name + tagline + address/phone).
export const TENANT_PDF = { ...(tenant.pdf || {}), logoUrl: tenant.logoUrl };
